import React from 'react';
import { Header } from '../components/Header';
import './LegalScreen.css';

export const PrivacyPolicyScreen: React.FC = () => {
  return (
    <div className="legal-screen">
      <div className="legal-container">
        <Header title="Privacy Policy" />
        <p className="legal-date" style={{ textAlign: 'center', marginTop: '16px', color: 'var(--color-text-secondary)' }}>Effective Date: October 24, 2025</p>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to Alveon Financial Tracker ("Alveon", "we", "our", or "us"). 
              We respect your privacy and are committed to protecting your personal data. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our financial tracking application.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> When you create an account, we collect your email address, name, and authentication credentials</li>
              <li><strong>Financial Data:</strong> Transaction records, income information, expense categories, financial goals, and budget settings</li>
              <li><strong>Profile Information:</strong> User preferences, currency settings, timezone, and language preferences</li>
            </ul>

            <h3>2.2 Information from Third-Party Services</h3>
            <ul>
              <li><strong>Telegram:</strong> User ID, username, first name, profile photo (when using Telegram authentication)</li>
              <li><strong>Google OAuth:</strong> Email address, name, profile picture (when using Google Sign-In)</li>
            </ul>

            <h3>2.3 Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Information about how you interact with our app, features used, and session duration</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, and IP address</li>
              <li><strong>Cookies:</strong> We use essential cookies for authentication and session management</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and maintain our financial tracking services</li>
              <li>Authenticate users and manage account security</li>
              <li>Process and store your financial transactions</li>
              <li>Send notifications about budget limits, goals, and scheduled reminders</li>
              <li>Synchronize data across your devices</li>
              <li>Improve our services and develop new features</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Comply with legal obligations and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Storage and Security</h2>
            
            <h3>4.1 Where We Store Your Data</h3>
            <p>
              Your data is securely stored in PostgreSQL databases hosted on Render.com servers. 
              All data transmission is encrypted using industry-standard HTTPS/TLS protocols.
            </p>

            <h3>4.2 Security Measures</h3>
            <ul>
              <li>Passwords are encrypted using bcrypt hashing</li>
              <li>JWT tokens are used for secure authentication</li>
              <li>Database access is restricted and monitored</li>
              <li>Regular security audits and updates</li>
              <li>Tokens are invalidated on password change</li>
            </ul>

            <h3>4.3 Data Retention</h3>
            <p>
              We retain your personal data for as long as your account is active or as needed to 
              provide services. You can request deletion of your account and data at any time.
            </p>
          </section>

          <section>
            <h2>5. Data Sharing and Disclosure</h2>
            
            <h3>5.1 We Do NOT Share Your Data</h3>
            <p>We do not sell, trade, or rent your personal information to third parties.</p>

            <h3>5.2 Limited Disclosure</h3>
            <p>We may disclose your information only in the following circumstances:</p>
            <ul>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
              <li><strong>Service Providers:</strong> Third-party services that help us operate (e.g., hosting providers, payment processors) under strict confidentiality agreements</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulations</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets (users will be notified)</li>
              <li><strong>Protection:</strong> To protect our rights, safety, and property, or those of our users</li>
            </ul>
          </section>

          <section>
            <h2>6. Your Rights and Choices</h2>
            
            <h3>6.1 Access and Control</h3>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
              <li><strong>Export:</strong> Download your financial data in Excel format</li>
              <li><strong>Portability:</strong> Transfer your data to another service</li>
              <li><strong>Opt-out:</strong> Disable notifications and marketing communications</li>
            </ul>

            <h3>6.2 Account Deletion</h3>
            <p>
              To delete your account, go to Settings → Delete Account. 
              This action is permanent and will remove all your data from our servers.
            </p>
          </section>

          <section>
            <h2>7. Google User Data - Detailed Disclosure</h2>
            
            <h3>7.1 What Google Data We Access</h3>
            <p>When you sign in with Google OAuth, we request access to the following scopes:</p>
            <ul>
              <li><strong>openid</strong> - To verify your identity</li>
              <li><strong>https://www.googleapis.com/auth/userinfo.email</strong> - To access your email address</li>
              <li><strong>https://www.googleapis.com/auth/userinfo.profile</strong> - To access your name and profile picture</li>
            </ul>

            <h3>7.2 How We Use Google User Data</h3>
            <p>We use the Google user data for the following specific purposes:</p>
            <ul>
              <li><strong>Email Address:</strong> Used for account identification, authentication, and to send important notifications about your account. We do not use your email for marketing purposes without your explicit consent.</li>
              <li><strong>Name:</strong> Displayed in your profile and used for personalization within the app. Your name helps us provide a personalized experience when you interact with our financial tracking features.</li>
              <li><strong>Profile Picture:</strong> Displayed as your avatar in the application interface to help you identify your account visually.</li>
              <li><strong>Google User ID (sub):</strong> Used as a unique identifier to link your Google account to your Alveon account securely. This allows you to sign in using your Google credentials.</li>
            </ul>
            
            <p><strong>Restricted Use:</strong> We only use Google user data to provide our financial tracking service. We do not use Google user data for advertising, marketing, or any other purposes beyond what is necessary for the core functionality of our application.</p>

            <h3>7.3 How We Store Google User Data</h3>
            <ul>
              <li><strong>Database:</strong> Google user data is stored in our PostgreSQL database hosted on Render.com</li>
              <li><strong>Encryption:</strong> All data is transmitted over HTTPS/TLS and stored with industry-standard encryption</li>
              <li><strong>Access Tokens:</strong> OAuth access tokens and refresh tokens are securely stored and used only for authentication purposes</li>
              <li><strong>Retention:</strong> Google user data is retained as long as your account is active. You can delete your account at any time</li>
            </ul>

            <h3>7.4 How We Share Google User Data</h3>
            <p><strong>We do NOT share your Google user data with any third parties.</strong></p>
            <p>Your Google user data is used exclusively within the Alveon application and is not:</p>
            <ul>
              <li>Sold to advertisers or data brokers</li>
              <li>Shared with marketing companies</li>
              <li>Used for purposes other than providing our financial tracking service</li>
              <li>Transferred to other applications or services</li>
            </ul>

            <h3>7.5 Revoking Google Access</h3>
            <p>You can revoke Alveon's access to your Google data at any time by:</p>
            <ul>
              <li>Visiting your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account Permissions</a> page</li>
              <li>Deleting your Alveon account from Settings → Delete Account</li>
              <li>Unlinking your Google account from Settings → Connected Accounts</li>
            </ul>

            <h3>7.6 Other Third-Party Services</h3>
            
            <h4>Telegram Authentication</h4>
            <p>
              When you sign in with Telegram, we receive your Telegram User ID, username, first name, 
              and profile photo. This data is used solely for authentication and personalization.
            </p>
            <p><a href="https://telegram.org/privacy" target="_blank" rel="noopener noreferrer">Telegram Privacy Policy</a></p>

            <h4>AI Services (OpenAI)</h4>
            <p>
              Our AI chat feature uses OpenAI API to provide financial insights. Conversations are 
              processed to answer your questions but are not used to train AI models. We do not share 
              your financial transaction data with OpenAI.
            </p>
            <p><a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer">OpenAI Privacy Policy</a></p>

            <h4>Google Privacy Policy</h4>
            <p>
              For more information about how Google handles your data, please review: 
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
            </p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>
              Alveon is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected 
              information from a child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>
              Your data may be transferred and stored on servers located outside your country. 
              By using Alveon, you consent to the transfer of your information to countries that 
              may have different data protection laws.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              significant changes by posting a notice in the app or sending an email. 
              Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our data practices, contact us:</p>
            <ul className="contact-info">
              <li><strong>Email:</strong> support@alveon.app</li>
              <li><strong>Telegram:</strong> @AlveonSupport</li>
              <li><strong>Website:</strong> <a href="https://app.alveon.app" target="_blank" rel="noopener noreferrer">https://app.alveon.app</a></li>
            </ul>
          </section>

          <section>
            <h2>12. GDPR Compliance (EU Users)</h2>
            <p>If you are located in the European Economic Area (EEA), you have additional rights under GDPR:</p>
            <ul>
              <li>Right to object to processing</li>
              <li>Right to restrict processing</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
              <li>Right to withdraw consent at any time</li>
            </ul>
            <p>
              Our legal basis for processing your data is your consent and the necessity to 
              perform our contract with you (providing financial tracking services).
            </p>
          </section>

          <section>
            <h2>13. California Privacy Rights (CCPA)</h2>
            <p>California residents have additional rights:</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to equal service and price</li>
            </ul>
            <p><strong>Note:</strong> We do not sell your personal information.</p>
          </section>
        </div>

        <footer className="legal-footer">
          <p>Last updated: October 24, 2025</p>
          <p>© 2025 Alveon Financial Tracker. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

